import { type ComponentPropsWithoutRef, memo, type MouseEvent, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import Markdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { hideIncompleteMarkdownLink } from '../../domain/streamingMarkdown'

// SALESCORE には回答の Markdown の見た目の決まりがないので、playground の styles.css の .chat-markdown を Tailwind で書いた
const MARKDOWN_CLASS_NAME = [
  '[&>:first-child]:mt-0 [&>:last-child]:mb-0',
  '[&_ol]:my-2 [&_p]:my-2 [&_pre]:my-2 [&_table]:my-2 [&_ul]:my-2',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6',
  '[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-[0.9em]',
  '[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-100 [&_pre]:px-3 [&_pre]:py-2 [&_pre_code]:p-0',
  '[&_td]:border [&_td]:border-solid [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1',
  '[&_th]:border [&_th]:border-solid [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1',
].join(' ')

/**
 * マウスで押してもフォーカスを移さない。ブラウザは mousedown でフォーカスを移すので、その既定の動作だけを止める。
 * 回答の中のリンクを押しても、入力欄のフォーカスが外れず、続けて入力できるようにするためである。クリックとキーボードの操作は変わらない
 */
export const keepFocusOnMouseDown = (event: MouseEvent): void => {
  event.preventDefault()
}

const MarkdownLink = ({ href, children }: ComponentPropsWithoutRef<'a'>): ReactElement => (
  <a href={href} target="_blank" rel="noreferrer" onMouseDown={keepFocusOnMouseDown}>
    {children}
  </a>
)

// 画像は読み込まず、リンクとして出す。出しただけでブラウザが URL を読みに行くので、
// プロンプトインジェクションで回答に外部の画像を入れさせると、URL に載せた会話の内容が外部に送られてしまう
const MarkdownImageLink = ({ src, alt }: ComponentPropsWithoutRef<'img'>): ReactElement => {
  const { t } = useTranslation()
  return <MarkdownLink href={src}>{alt === undefined || alt.trim() === '' ? t(`（画像）`) : alt}</MarkdownLink>
}

// リンクと画像を描く部品とプラグイン。描くたびに作り直すと、React が別の部品とみなして <a> を作り直すので、
// 書いている途中の回答で描き直しが続く間、押している最中のリンクが入れ替わってクリックが届かない。ファイルの最上位で1回だけ作る
const MARKDOWN_COMPONENTS: Components = { a: MarkdownLink, img: MarkdownImageLink }
const REMARK_PLUGINS = [remarkGfm]

/**
 * エージェントの回答を Markdown として表示する。playground の MarkdownText から移した。
 * remark-gfm で、文中の URL をリンクにし、表も表示する。リンクは別のタブで開く。
 * 同じ組織の画面へのリンクを画面の中で開くことと、view の名前と種別を出すことは、#10116 で足す。
 * isStreaming（書いている途中）の間は、末尾の書きかけのリンクを外して出す
 */
const AgentMarkdownView = ({ text, isStreaming }: { text: string; isStreaming: boolean }): ReactElement => (
  <div className={MARKDOWN_CLASS_NAME}>
    <Markdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
      {isStreaming ? hideIncompleteMarkdownLink(text) : text}
    </Markdown>
  </div>
)

export const AgentMarkdown = memo(AgentMarkdownView)
